'use client';

import Image from 'next/image';
import { memo, type MouseEvent, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { TrackedAnchor } from '@/components/ancher/tracked-anchor';
import { customRichTextTags } from '@/utils/i18n';
import { cn } from '@lib/utils';
import { TEN_YEARS_POSITION, storyItemKey } from '../../_const/stories';
import DayBadge from '../../_components/day-badge';
import { openStory, warmStoryPoster } from '../../_utils/story-transition';

export type StoryCardData = {
  day: number;
  releaseAt: number;
  // 공개된 카드만 값이 있다
  images: { thumbnail: string; poster: string } | null;
};

const CARD_CLASS =
  'relative block h-[350px] w-[300px] rounded-[26px] bg-white text-left shadow-[0_0_4px_rgba(0,0,0,0.04),0_12px_12px_-8px_rgba(0,0,0,0.08)]';

// 캐러셀을 넘길 때마다 보드가 다시 그려지므로, 값이 같은 카드는 건너뛴다(props 가 전부 원시값·고정 참조)
export default memo(function StoryCard({
  card,
  dDay,
}: {
  card: StoryCardData;
  dDay: number;
}) {
  const t = useTranslations('TenYears.stories');
  const router = useRouter();
  const { day, images } = card;
  const key = storyItemKey(day);
  const href = `/10years/story/${day}`;

  const content = (
    <>
      {images ? (
        <CardPhotoFrame>
          <Image
            src={images.thumbnail}
            alt=""
            fill
            sizes="276px"
            className="object-cover"
          />
          <DayBadge day={day} className="absolute right-[18px] top-[17px]" />
        </CardPhotoFrame>
      ) : (
        <CardPhotoFrame locked>
          <span className="mx-auto mt-[45px] block w-[104px] bg-gradient-to-r from-[#ff97aa] to-[#ff3713] bg-clip-text text-center text-transparent">
            <span className="block text-[18px] font-medium leading-[28px] tracking-[-0.256px]">
              {t('untilRelease')}
            </span>
            <span className="block text-[36px] font-semibold leading-[44px] tracking-[-0.784px]">
              {dDay > 0 ? t('dDay', { count: dDay }) : t('dDayToday')}
            </span>
          </span>
        </CardPhotoFrame>
      )}
      <CardOverlay />
      <span className="absolute left-6 top-[208px] block w-[250px]">
        <CardTitle>
          {t.rich(`items.${key}.title`, customRichTextTags)}
        </CardTitle>
        <CardByline
          name={t(`items.${key}.name`)}
          role={t(`items.${key}.role`)}
        />
      </span>
    </>
  );

  if (!images) {
    // group/story 가 없어 호버에 반응하지 않는다
    return (
      <div data-locked className={CARD_CLASS}>
        {content}
      </div>
    );
  }

  const openWithTransition = (event: MouseEvent<HTMLAnchorElement>) => {
    // 새 탭·새 창 열기는 브라우저에 맡긴다
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    openStory(
      day,
      event.currentTarget.querySelector<HTMLElement>('.ty-story-photo'),
      function pushStory() {
        router.push(href, { scroll: false });
      },
    );
  };

  return (
    <TrackedAnchor
      href={href}
      onClick={openWithTransition}
      onPointerEnter={() => warmStoryPoster(images.poster)}
      onFocus={() => warmStoryPoster(images.poster)}
      label={`10years-stories-open-day-${day}`}
      position={TEN_YEARS_POSITION}
      className={cn(
        'group/story',
        CARD_CLASS,
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white',
      )}
    >
      {content}
    </TrackedAnchor>
  );
});

// ty-story-photo: 카드를 누를 때 사진 틀을 찾는 표식(View Transition 출발점)
function CardPhotoFrame({
  locked,
  children,
}: {
  locked?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'ty-story-photo absolute left-3 top-3 block h-[253px] w-[276px] overflow-hidden rounded-t-[20px] rounded-bl-[20px]',
        // 호버: 시안 4990-956·4990-990. 전환 시간은 시안에 없는 임시값(디자이너 확인 대기)
        'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)] [@media(hover:hover)]:group-hover/story:-translate-y-[24.3px] [@media(hover:hover)]:group-hover/story:rotate-[1.07deg] [@media(hover:hover)]:group-focus-visible/story:-translate-y-[24.3px] [@media(hover:hover)]:group-focus-visible/story:rotate-[1.07deg]',
        // 모바일 캐러셀은 호버가 없어 가운데 카드(stories-board data-current)를 호버 모양으로 보인다. 잠긴 카드는 제외
        locked
          ? 'bg-[rgba(255,141,141,0.2)]'
          : 'max-lg:group-data-[current]/slot:-translate-y-[24.3px] max-lg:group-data-[current]/slot:rotate-[1.07deg]',
      )}
    >
      {children}
    </span>
  );
}

// 시안 벡터 그대로
function CardOverlay() {
  return (
    <Image
      src="/images/10years/stories/card-overlay.svg"
      alt=""
      width={300}
      height={205}
      className="absolute left-0 top-[145px] h-[205px] w-[300px]"
    />
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <span className="block text-[24px] font-semibold leading-8 tracking-[-0.464px] text-3b-text-primary">
      {children}
    </span>
  );
}

function CardByline({ name, role }: { name: string; role: string }) {
  return (
    <span className="mt-1.5 block text-[14px] font-medium leading-[22px] tracking-[-0.128px]">
      <span className="block text-3b-text-primary">{name}</span>
      {role && <span className="block text-3b-text-disabled">{role}</span>}
    </span>
  );
}
